import Logger from '../logger';
import Utils from '../utils';

/**
 * @class ApiPlatform
 * @description Generic API module to perform network requests to a backend
 */
class ApiPlatform {

    /**
     * @example apiConfig
     * {
     baseUrl: `${process.env.NEON_API_BASE_URL}`, // default is dev api, which is set in webpack.config.js
     endpoints: {},
     urlEncodeParams: true,
     skipPrefixIndexParams: true,
     requestFailedMessage: 'An error occurred while processing your request.',

     // allow to use cross-domain cookies for authentication
     // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
     credentials: 'include',
     logoutRedirect: '/login',
     loginRedirect: '/login',
     loggingExclusions: {

     // endpoints of api errors should not be logged at all
     endpoints: [],

     // header fields from the api call that failed that should be excluded from logging
     headers: [],

     // fields from the post body of an api error that should be excluded from logging
     postBody: [
     'password'
     ],

     // fields from the response body of an api error that should be excluded from logging
     responseBody: []
     }
     }
     * @param {string} apiName - api name
     * @param {Object} apiConfig - config
     * @param {string} apiConfig.baseUrl - base url
     * @param {Object} apiConfig.endpoints - key value pair endpoints
     * @param {boolean} [apiConfig.urlEncodeParams] - url encode parameters
     * @param {boolean} [apiConfig.skipPrefixIndexParams] - skip the index of array parameters
     * @param {string} apiConfig.requestFailedMessage - default message returned when requests fails
     * @param {string} apiConfig.credentials - credentials options for htt requests (see fetch documentation)
     * @param {string} apiConfig.logoutRedirect - logout path
     * @param {string} apiConfig.maintenanceRedirect - maintenance path
     * @param {string} apiConfig.loginRedirect - login path
     * @param {Object} apiConfig.loggingExclusions - config
     * @param {array} apiConfig.loggingExclusions.endpoints - exclude request logging information config
     * @param {array} apiConfig.loggingExclusions.headers - list of header strings to exclude
     * @param {array} apiConfig.loggingExclusions.postBody - list of post body keys to exclude
     * @param {array} apiConfig.loggingExclusions.responseBody - list of response body keys to exclude
     * @param {AbstractAuthenticator} authenticator - authenticator
     * @param {AbstractAuthoriser} authoriser - authoriser
     */
    constructor(apiName, apiConfig, authenticator, authoriser) {
        this.logger = Logger.instance;
        this.config = apiConfig;
        this.authenticator = authenticator;
        this.authoriser = authoriser;

        if (!this.config) {
            throw new Error('apiConfig is not set. Cannot create API instance.');
        }
    }

    /**
     * Returns the API config of this API instance.
     * @returns {Object} API config
     */
    getConfig() {
        return this.config;
    }

    /**
     * Returns the baseUrl of this API instance.
     * @returns {string} base url
     */
    getBaseUrl() {
        return this.config.baseUrl;
    }

    /**
     * Returns the endpoints of this API instance.
     * @returns {Object} key-value pair based endpoints
     */
    getEndpoints() {
        return this.config.endpoints;
    }

    /**
     * Returns the authenticator of this API instance.
     * @returns {AbstractAuthenticator} authenticator instance
     */
    getAuthenticator() {
        return this.authenticator;
    }

    /**
     * Returns the authoriser of this API instance.
     * @returns {AbstractAuthoriser} authoriser instance
     */
    getAuthoriser() {
        return this.authoriser;
    }

    /**
     * Returns whether the given http status code is considered a warning code
     *
     * @param {number} code - http status code
     * @returns {boolean} is a warning code
     * @private
     */
    static isWarningCode(code) {
        return ((code >= 300 && code <= 399) || code === 404);
    }

    /**
     * Returns whether the given http status code is considered an error code
     *
     * @param {number} code - http status code
     * @returns {boolean} is an error code
     * @private
     */
    static isErrorCode(code) {
        return (code >= 400 && code !== 404);
    }

    /**
     * Logs a warning to the logger with the given message
     * @param {string} type - type 'warning' or 'error'
     * @param {string} [message] - log message
     * @param {string} [url] - url
     * @param {Object} [options] - request options
     * @param {Object} [response] - response object
     * @param {Object} [jsonResponse] - json object response
     * @param {Object} [error] - error (type of Error)
     * @returns {undefined}
     */
    logApiMessage(type, message = '', url = '', options = {}, response = {}, jsonResponse = {}, error = null) {
        const loggingExclusions = this.config.loggingExclusions;

        // check if this url contains excluded endpoints, stop if so
        for (let i = 0; i < loggingExclusions.endpoints.length; i++) {
            const endpoint = loggingExclusions.endpoints[i];

            if (url.includes(endpoint)) {
                return;
            }
        }

        // check if there are headers that should be excluded from the logging
        loggingExclusions.headers.forEach(header => {
            if (options.headers && options.headers[header]) {
                options.headers[header] = null;
                delete options.headers[header];
            }
        });

        // check if there are post body values that should be excluded from the logging
        loggingExclusions.postBody.forEach(fieldKey => {
            if (options.payload && options.payload.data && options.payload.data[fieldKey]) {
                options.payload.data[fieldKey] = null;
                delete options.payload.data[fieldKey];
            }
        });

        // check if there are response body values that should be excluded from the logging
        loggingExclusions.responseBody.forEach(fieldKey => {
            if (jsonResponse[fieldKey]) {
                jsonResponse[fieldKey] = null;
                delete jsonResponse[fieldKey];
            }
        });

        // stringify the response if necessary
        if (typeof jsonResponse === 'object') {
            jsonResponse = JSON.stringify(jsonResponse);
        }

        let loggedInUserId = '';

        if (this.getAuthenticator().getUser()) {
            loggedInUserId = this.getAuthenticator().getUser().getId() || '';
        }

        // call the logger
        if (type === 'warning') {
            this.logger.warning({
                component: 'ApiPlatform',
                message,
                requestUrl: url,
                requestOptions: JSON.stringify(options),
                responseStatus: response.status || 0,
                responseText: response.statusText || '',
                responseBody: jsonResponse,
                loggedInUserId,
                error

            });
        } else if (type === 'error') {
            this.logger.error({
                component: 'ApiPlatform',
                message,
                requestUrl: url,
                requestOptions: JSON.stringify(options),
                responseStatus: response.status || 0,
                responseText: response.statusText || '',
                responseBody: jsonResponse,
                loggedInUserId,
                error
            });
        }
    }

    /**
     * Executes the fetch call to perform the network request
     *
     * @param {string} url - url with endpoint
     * @param {string} method - http method [get, put, post, patch, options]
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.payload] - post body object
     * @param {Object} [options.payload.data] - object or array with un-serialised content
     * @param {Object} [options.payload.type] - type of the payload [json, form]
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @param {Object} [options.returnResponseOnError] - optionally return the actual response instead of generic one
     * @private
     * @returns {Promise} network request promise
     */
    executeRequest(url, method, options = {}) {
        const self = this;
        const cachedRequest = {
            url,
            method,
            options
        };

        return new Promise((resolve, reject) => {

            // parameters for fetch request
            let requestParams = {
                method: method.toUpperCase(), // to avoid patch verb not normalised to uppercase
                headers: {
                    Accept: 'application/json'
                }
            };

            // allow to use cross-domain cookies for authentication
            // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
            if (this.config.credentials) {
                requestParams.credentials = this.config.credentials;
            }

            // builds the url with the given identifiers and parameters
            const parsedUrl = self.buildURL(url, options.urlParams);

            // reject when there were still identifiers in the url, that were not replaced
            if (parsedUrl === null) {

                // Log to logger and reject with a proper error message
                self.logApiMessage(
                    'error',
                    'Build url failed. Could not parse all identifiers',
                    url,
                    options,
                    {},
                    {},
                    new Error('Build url failed. Could not parse all identifiers')
                );

                return reject(new Error(self.config.requestFailedMessage));
            }

            // parse payload
            if (options.payload) {
                try {
                    requestParams = self.buildPayload(requestParams, options.payload);
                } catch (e) {
                    return reject(new Error(self.config.requestFailedMessage));
                }
            }

            // Loop through and set the custom headers
            if (options.headers) {
                requestParams = this.buildRequestHeaders(requestParams, options.headers);
            }

            requestParams = this.addTokenHeader(requestParams);

            // execute the request
            return fetch(parsedUrl, requestParams).then(response => {

                // todo: handle content disposition / blob responses

                // return maintenance mode if the backend is not available
                // note: can only be tested if BE returns a 503 response, not when BE is really down
                if (response.status === 503) {
                    if (self.getConfig().maintenanceRedirect) {
                        window.location = self.getConfig().maintenanceRedirect;
                    } else {
                        return resolve({
                            maintenance: true
                        });
                    }
                }

                // try to get and return the json response
                return response.json().then(json => {

                    // always store csrf token when this call did not succeed
                    // so we can use it in token renewal calls, until te User object is constructed again
                    if (response.status !== 200 && response.headers.get('token')) {
                        if (this.getAuthenticator().getUser()) {
                            this.getAuthenticator().getUser().setCsrfToken(response.headers.get('token'));
                        } else {
                            this.getAuthenticator().setCsrfToken(response.headers.get('token'));
                        }
                    }

                    // renew token if 401 was returned, and try the same API call again
                    if (response.status === 401) {
                        if (!options.retry) {
                            return self.authenticator.refreshAndGetUser().then(() => {
                                cachedRequest.options.retry = true;

                                // call the original api request again, and connect the resolve/error methods
                                return self.executeRequest(cachedRequest.url, cachedRequest.method, cachedRequest.options).then(newJsonResponse => {
                                    resolve(newJsonResponse);
                                }).catch(error => {
                                    reject(error);
                                });
                            }).catch(error => {

                                // Token refresh failed, might be due to expiry, log message and redirect
                                self.logApiMessage('warning', 'token refresh failed during an api call', parsedUrl, options, {}, {}, error);

                                // entra token invalid, return custom error
                                if (response.url.includes('microsoft')) {
                                    return resolve({
                                        errors: ['Invalid Entra token']
                                    });
                                }

                                window.location.href = self.authenticator.getLoginRedirect();
                            });
                        }

                        // we already retried and we still get 401. Consider this logged out...
                        return self.authenticator.logout('forced').then(() => {
                            window.location = self.getConfig().logoutRedirect;
                        });
                    }

                    // check if this was an input validation error
                    if ([400, 409, 422].includes(response.status) || json.detail) {
                        self.logApiMessage('warning', 'API call succeeded but with 400, 409 or 422 response', parsedUrl, options, response, json, null);

                        let errors = [];

                        if (json.detail) {
                            errors.push(json.detail);
                        }

                        if (json.violations) {
                            errors = json.violations;
                        }

                        // resolve in case of errors, since the response is valid
                        return resolve({
                            errors,
                            ...json
                        });
                    }

                    // log and/or reject based on our http status code checks
                    if (ApiPlatform.isWarningCode(response.status)) {
                        self.logApiMessage('warning', 'API call succeeded but with a warning flagged response code', parsedUrl, options, response, json, null);

                    } else if (ApiPlatform.isErrorCode(response.status) || !response.ok) {

                        if (options.returnResponseOnError) {
                            return reject(json);
                        }

                        self.logApiMessage('error', 'API call succeeded but with an error flagged response code', parsedUrl, options, response, json, null);
                        return reject(new Error(self.config.requestFailedMessage));
                    }

                    // return json by default (if it wasn't rejected before)
                    return resolve(json);

                }).catch(error => {

                    // we always expect json, except on 204 no content response
                    if (response.status === 204) {
                        return resolve({});
                    }

                    // consider this as a failed request
                    self.logApiMessage('error', 'Parsing JSON response failed', parsedUrl, options, response, {}, error);
                    return reject(new Error(self.config.requestFailedMessage));
                });

            }).catch(error => {
                self.logApiMessage('error', 'Unexpected API request error', parsedUrl, options, {}, {}, error);
                return reject(new Error(self.config.requestFailedMessage));
            });
        });
    }

    /**
     * Builds and appends the post body and headers to the given request parameters
     * @param {Object} requestParams - request parameters
     * @param {Object} payload - post body object
     * @param {Object} payload.data - object or array with un-serialised content
     * @param {string} payload.type - type of the payload [json, form]
     * @param {string} [payload.formKey] - optional form key
     * @returns {Object} Request params with post body appended
     */
    buildPayload(requestParams, payload = {}) {

        // return by default
        if (!payload) {
            return requestParams;
        }

        if (payload.type === 'json') {
            requestParams.body = JSON.stringify(payload.data);
            requestParams.headers['Content-Type'] = 'application/json';
        } else if (payload.type === 'merge-patch+json') {
            requestParams.body = JSON.stringify(payload.data);
            requestParams.headers['Content-Type'] = 'application/merge-patch+json';
        } else {

            const error = new Error(`Could not parse post body (payload.data). payload.type was not given on request: ${JSON.stringify(requestParams)}`);

            // Log to logger and reject with a proper error message
            this.logApiMessage('error', 'Could not parse post data', '', requestParams, {}, {}, error);
            throw error;
        }

        return requestParams;
    }

    /**
     * Builds and appends the headers to the given request parameters
     * @param {Object} requestParams - request parameters
     * @param {Object} headers - headers as key-value pairs
     * @returns {Object} Request params with post body appended
     */
    buildRequestHeaders(requestParams, headers = {}) {

        // return by default
        if (!headers) {
            return requestParams;
        }

        // For each header property, read and set key value
        for (const headerKey in headers) {

            // use hasOwnProperty so default object methods and properties (built-in JS) are skipped
            // https://stackoverflow.com/questions/684672/how-do-i-loop-through-or-enumerate-a-javascript-object
            if (headers.hasOwnProperty(headerKey)) {
                requestParams.headers[headerKey] = headers[headerKey];
            }
        }

        return requestParams;
    }

    addTokenHeader(requestParams) {
        const user = this.getAuthenticator().getUser();
        let token = null;

        if (user !== null) {
            token = user.getCsrfToken();
        }

        if (token === null) {
            token = this.getAuthenticator().getCsrfToken();
        }

        const methods = ['post', 'put', 'patch', 'delete'];

        if (methods.includes(requestParams.method.toLowerCase()) && token !== null) {
            requestParams.headers.token = token;
        }

        return requestParams;
    }

    /**
     * Builds the url with the given identifiers and parameters
     * identifiers: {id:1} will replace '{id}' in the url with 1
     * parameters: all parameters (including arrays) are serialised, eventually with url encoding
     * and/or index parameters. Index parameters are used in arrays. With this skip option on the result will be
     * k[]=v rather than k[0]=v
     *
     * @param {string} url - url to replace and append the given identifiers and parameters
     * @param {Object} urlParams - parameters object for url identifiers and parameters
     * @param {Object} [urlParams.identifiers] - Key value pairs for url identifiers
     * @param {Object} [urlParams.parameters] - Key value pairs for url parameters (child array allowed)
     * @returns {string|null} built url or null in case of unknown url identifiers
     * @private
     */
    buildURL(url, urlParams) {
        let buildUrl = url;

        if (urlParams) {

            const urlIdentifiers = urlParams.identifiers,
                urlParameters = urlParams.parameters;

            // replace url identifiers (NOT parameters)
            if (urlIdentifiers) {

                // map identifiers (e.g. id becomes {id})
                const keys = Object.keys(urlIdentifiers).map(x => `{${x}}`);

                // replace all identifiers with given values
                buildUrl = Utils.replaceString(
                    buildUrl,
                    keys,
                    Object.values(urlIdentifiers)
                );
            }

            // append url parameters without url encoding
            if (urlParameters && Object.keys(urlParameters).length && buildUrl !== null) {
                buildUrl = Utils.buildQueryString(
                    buildUrl,
                    urlParameters,
                    this.config.urlEncodeParams,
                    this.config.skipPrefixIndexParams
                );
            }
        }

        // reset build url and return null if there are unreplaced identifiers
        // ~ shifts the bits like: -(i + 1) (invert plus 1). Makes it superfluous to check on -1 or >= 0
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_Operators#Bitwise_NOT
        if (~buildUrl.indexOf('{') && ~buildUrl.indexOf('}')) {
            buildUrl = null;
        }

        return buildUrl;
    }

    /**
     * Executes a get request to the given url + endpoint with the given payload/headers
     *
     * @param {string} baseUrl - baseUrl
     * @param {string} endpoint - endpoint
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.payload] - post body object
     * @param {Object} [options.payload.data] - object or array with un-serialised content
     * @param {Object} [options.payload.type] - type of the payload [json, form]
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @returns {Promise} network request promise
     */
    get(baseUrl, endpoint, options) {
        return this.executeRequest(baseUrl + endpoint, 'get', options);
    }

    /**
     * Executes a post request to the given url + endpoint with the given payload/headers
     *
     * @param {string} baseUrl - baseUrl
     * @param {string} endpoint - endpoint
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.payload] - post body object
     * @param {Object} [options.payload.data] - object or array with un-serialised content
     * @param {Object} [options.payload.type] - type of the payload [json, form]
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @returns {Promise} network request promise
     */
    post(baseUrl, endpoint, options) {
        return this.executeRequest(baseUrl + endpoint, 'post', options);
    }

    /**
     * Executes a put request to the given url + endpoint with the given payload/headers
     *
     * @param {string} baseUrl - baseUrl
     * @param {string} endpoint - endpoint
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.payload] - post body object
     * @param {Object} [options.payload.data] - object or array with un-serialised content
     * @param {Object} [options.payload.type] - type of the payload [json, form]
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @returns {Promise} network request promise
     */
    put(baseUrl, endpoint, options) {
        return this.executeRequest(baseUrl + endpoint, 'put', options);
    }

    /**
     * Executes a patch request to the given url + endpoint with the given payload/headers
     *
     * @param {string} baseUrl - baseUrl
     * @param {string} endpoint - endpoint
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.payload] - patch body object
     * @param {Object} [options.payload.data] - object or array with un-serialised content
     * @param {Object} [options.payload.type] - type of the payload [json, form]
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @returns {Promise} network request promise
     */
    patch(baseUrl, endpoint, options) {
        return this.executeRequest(baseUrl + endpoint, 'patch', options);
    }

    /**
     * Executes a delete request to the given url + endpoint with the given headers
     * The method is called remove, because delete is a reserved keyword in ecmascript 15.
     *
     * @param {string} baseUrl - baseUrl
     * @param {string} endpoint - endpoint
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @returns {Promise} network request promise
     */
    remove(baseUrl, endpoint, options) {
        return this.executeRequest(baseUrl + endpoint, 'delete', options);
    }

    /**
     * Executes an options request to the given url + endpoint with the given payload/headers
     *
     * @param {string} baseUrl - baseUrl
     * @param {string} endpoint - endpoint
     * @param {Object} options - options object
     * @param {Object} [options.urlParams] - url parameters object
     * @param {Object} [options.urlParams.parameters] - url parameters to append on the end of the url
     * @param {Object} [options.urlParams.identifiers] - url identifiers to replace inside the url or endpoint
     * @param {Object} [options.payload] - post body object
     * @param {Object} [options.payload.data] - object or array with un-serialised content
     * @param {Object} [options.payload.type] - type of the payload [json, form]
     * @param {Object} [options.headers] - key value pairs of headers to be set
     * @returns {Promise} network request promise
     */
    options(baseUrl, endpoint, options) {
        return this.executeRequest(baseUrl + endpoint, 'options', options);
    }
}

export default ApiPlatform;
